/*
 * Do not remove or alter the notices in this preamble.
 * This software code regards iO Custom Software.
 * Copyright © 2026 iO The Netherlands B.V. and/or its affiliates.
 * www.iodigital.com All rights reserved. License grant and user rights and obligations
 * according to applicable license agreement. Please contact legal@iodigital.com for
 * questions regarding license and user rights.
 */

import { consume } from '@lit/context';
import { t } from 'i18next';
import { css, html, LitElement, nothing, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { createRef, Ref, ref } from 'lit/directives/ref.js';

import { QuantityPerVariantStepDto, StepEnum } from '@/api-sdk';
import { FgFullscreenImage } from '@/components';
import {
  iconCaret,
  iconCheckmarkCircle,
  iconExclamationTriangle,
} from '@/components/icons';
import {
  ConfiguratorContextType,
  configuratorGlobal,
  global,
  GlobalComponentType,
  MobileContext,
} from '@/context';
import { StoreController } from '@/controllers';
import {
  ANIMATION_SPEED,
  BREAKPOINT_EXTRA_LARGE_PX,
  BREAKPOINT_LARGE_PX,
  BREAKPOINT_MEDIUM_PX,
} from '@/settings';
import { configuratorStore } from '@/stores';
import {
  baseStyles,
  loadingStyles,
  rootVariablesStyles,
  typographyStyles,
} from '@/styles';
import { getCountryCode } from '@/utils';

import { QuantityStepMixin } from '../quantity/quantity-step-mixin';

import '@/components/configurator/fg-configurator-step';
import '@/components/configurator/step/wholesale/fg-step-header';
import '@/components/configurator/step/wholesale/quantity/fg-quantity-back-order';
import '@/components/configurator/step/wholesale/quantity/fg-quantity-in-option';
import '@/components/configurator/step/wholesale/quantity/fg-quantity-stock-notification-dialog';
import '@/components/configurator/step/wholesale/quantity/fg-quantity-stock-notification-link';
import '@/components/configurator/step/wholesale/quantity/fg-quantity-variant-input-message';
import '@/components/configurator/step/wholesale/quantity/fg-quantity-variants-input';
import '@/components/image/fg-image';

@customElement('fg-configurator-quantity-variant')
export class FgConfiguratorQuantityVariant extends QuantityStepMixin(
  LitElement,
) {
  private validationStoreController = new StoreController(
    this,
    configuratorStore,
    state => {
      this.showValidationMessage = state.showValidationMessage;
      this.validationMessages = state.validationMessages;
    },
  );

  /**
   * Used to render the step on the first load, when the call to de quantity data is not made and we have recieved the
   * data from the initial configurator call.
   *
   * @property renderData {@link QuantityPerVariantStepDto}
   */
  @property({ type: Object })
  renderData?: QuantityPerVariantStepDto;

  /**
   * Step type used to render the step title number using the {@link Step} type form the api-sdk
   *
   * @property stepType
   */
  @property({ type: String })
  stepType?: StepEnum.quantity_per_variant | StepEnum.print_option_per_position;

  /**
   * Object containing the quantity data per variant, this is used to render the step. This is received from the quantity
   * endpoint. See: {@link QuantityStepMixin} where this call is made.
   */
  @state()
  quantityData?: QuantityPerVariantStepDto;

  /**
   * Used to set the first render data that is received from the configurator, this is used to prevent on render a empty
   * quantity data and a possible layout shift.
   */
  @state()
  isSetByConfigurator = false;

  /**
   * Array containing al current state a variant block can have, this is used to set the active and the collapsed
   * (Mobile only) state
   */
  @state()
  variantStates: Array<{
    active: boolean;
    collapsed: boolean;
  }> = [];

  @state()
  validationMessages = configuratorStore.getState().validationMessages;

  @state()
  showValidationMessage = configuratorStore.getState().showValidationMessage;

  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  @consume({ context: configuratorGlobal.context, subscribe: true })
  configuratorContext!: ConfiguratorContextType;

  @consume({ context: MobileContext, subscribe: true })
  isMobile: boolean = false;

  private fullscreenImageDialogRefs: Ref<FgFullscreenImage>[] = [];

  /**
   * To set the active state per variant, the index is used to select the right variant and the selected boolean is used
   * to set it to true or false. Is selected is true the variant will use the active styling. If false it will default to
   * the normal styling.
   *
   * This is dispatched via the @quantity-change custom event from the fg-quantity-variants-input component.
   *
   * @param index number
   * @param selected boolean
   */
  setActiveState(index: number, selected: boolean): void {
    this.variantStates[index] = {
      active: selected,
      collapsed: this.variantStates[index]?.collapsed,
    };
  }

  resetActiveState(): void {
    if (this.variantStates.length > 0) {
      this.variantStates = [];
    }
  }

  /**
   * Toggle collapsible state for mobile only, this is used to expand and collapse the variant blocks on mobile.
   *
   * @param {number} index
   */
  toggleCollapsible(index: number): void {
    if (!this.isMobile) {
      return;
    }
    this.variantStates[index] = {
      active: this.variantStates[index]?.active || false,
      collapsed: !this.variantStates[index]?.collapsed || false,
    };
    this.requestUpdate();
  }

  /**
   * Render a quantity input message based on the current quantity in the fg-quantity-variants-input component.
   *
   * @param {number} quantity
   * @param {boolean} isQuantityAvailableForPurchase
   * @param {number} index
   * @returns TemplateResult
   */
  renderQuantityInputMessage(
    quantity: number,
    isQuantityAvailableForPurchase: boolean,
    index: number,
  ): TemplateResult | typeof nothing {
    if (
      this.stepType === StepEnum.quantity_per_variant &&
      this.quantityData?.options[index].stock?.canOrderWithoutStock
    ) {
      return nothing;
    }
    return html`
      ${quantity > 0 &&
      !isQuantityAvailableForPurchase &&
      this.variantStates[index]?.active
        ? html`
            <fg-quantity-variant-input-message
              part=${this.globalContext.layout}
            ></fg-quantity-variant-input-message>
          `
        : nothing}
    `;
  }

  render() {
    /**
     * To prevent an empty step we are using the renderData received from the configurator, now it wont happen that the
     * step is empty en prevent a layout shift.
     */
    if (!this.isSetByConfigurator) {
      this.quantityData = this.renderData;
      this.isSetByConfigurator = true;
    }
    const { layout, locale } = this.globalContext,
      { configurationId, salable } = this.configuratorContext,
      noPriceAndQuantityInput = !salable && layout === 'wholesale';

    return html`
      <fg-configurator-step part=${layout} .stepType=${this.stepType}>
        <fg-step-header
          part=${layout}
          slot="step-header"
          .stepType=${this.stepType}
          .hspMaximumQuantity=${this.quantityData?.hspMaximumQuantity}
        ></fg-step-header>
        <div class="wrapper">
          ${this.quantityData?.options.map((data, index) => {
            const {
              thumbnailImage,
              mainImage,
              variantCode,
              productCode,
              name,
              totalPrice,
              stock,
              quantity,
              queryString,
              isQuantityAvailableForPurchase,
            } = data;
            this.fullscreenImageDialogRefs[index] = createRef();

            if (configurationId && quantity > 0) {
              this.setActiveState(index, true);
            }

            const showFullScreenImageIcon =
              !this.isMobile ||
              (this.isMobile && this.variantStates[index]?.collapsed);
            return html`
              <div
                class="product-variant ${classMap({
                  active: this.variantStates[index]?.active,
                  expand: this.variantStates[index]?.collapsed,
                  noPriceAndQuantityInput: noPriceAndQuantityInput,
                })}"
              >
                <div
                  class="product-data"
                  @click=${() => this.toggleCollapsible(index)}
                >
                  ${showFullScreenImageIcon
                    ? html`
                        <fg-fullscreen-image
                          class="fullscreen-button"
                          .imageUrl=${mainImage}
                          ${ref(this.fullscreenImageDialogRefs[index])}
                        ></fg-fullscreen-image>
                      `
                    : ''}
                  ${thumbnailImage
                    ? html`
                        <fg-image
                          clickable
                          width="72"
                          height="72"
                          src=${thumbnailImage}
                          @fg-image-click-event=${(event: Event) => {
                            event.stopPropagation();
                            if (
                              (this.isMobile &&
                                this.variantStates[index]?.collapsed) ||
                              !this.isMobile
                            ) {
                              this.fullscreenImageDialogRefs[
                                index
                              ]?.value?.open(event);
                            }
                          }}
                        ></fg-image>
                      `
                    : html`
                        <span class="spacer"></span>
                      `}
                  <div>
                    <h3 class="h5">${name}</h3>
                    <span>${productCode}-${variantCode}</span>
                    ${quantity > 0
                      ? html`
                          <div class="product-current-quantity mobile-only">
                            <span>
                              ${!isQuantityAvailableForPurchase
                                ? html`
                                    ${iconExclamationTriangle}
                                  `
                                : nothing}
                              ${t('content.configurator.quantity', {
                                count: quantity,
                              })}
                            </span>
                            <strong>${totalPrice}</strong>
                          </div>
                        `
                      : nothing}
                    <div class="product-invalid-input mobile-only">
                      ${this.renderQuantityInputMessage(
                        quantity,
                        isQuantityAvailableForPurchase,
                        index,
                      )}
                    </div>
                    <span class="caret mobile-only">${iconCaret}</span>
                  </div>
                </div>
                <div class="product-stock">
                  ${stock?.quantity === null
                    ? nothing
                    : stock?.canOrderWithoutStock
                      ? html`
                          <div class="product-stock-amount">
                            <span class="stock">
                              ${iconCheckmarkCircle} ${t('labels.available')}
                            </span>
                          </div>
                        `
                      : stock?.quantity && stock.quantity > 0
                        ? html`
                            <div class="product-stock-amount">
                              <span class="mobile-only">
                                ${t('labels.stock')}:
                              </span>
                              <span class="stock">
                                ${iconCheckmarkCircle}${Intl.NumberFormat(
                                  getCountryCode(locale),
                                ).format(stock.quantity)}
                              </span>
                            </div>
                          `
                        : html`
                            <div class="product-stock-notify">
                              <span class="mobile-only">
                                ${t('labels.stock')}:
                              </span>
                              <fg-quantity-stock-notification-link
                                part=${layout}
                                .variantName=${name}
                                .variantCode=${variantCode}
                              ></fg-quantity-stock-notification-link>
                            </div>
                          `}
                  ${stock?.totalIncoming && stock?.totalIncoming > 0
                    ? html`
                        <fg-quantity-back-order
                          part=${layout}
                          .totalIncoming=${stock.totalIncoming}
                          .incomingStocks=${stock.incomingStocks}
                        ></fg-quantity-back-order>
                      `
                    : nothing}
                  ${stock?.totalOption
                    ? html`
                        <fg-quantity-in-option
                          part=${layout}
                          .inOption=${stock.totalOption}
                        ></fg-quantity-in-option>
                      `
                    : nothing}
                </div>
                ${noPriceAndQuantityInput
                  ? nothing
                  : html`
                      <div class="product-quantity">
                        <fg-quantity-variants-input
                          part=${layout}
                          index=${index}
                          .disabled=${(stock?.quantity === 0 &&
                            stock?.totalIncoming === 0 &&
                            !stock?.canOrderWithoutStock) ||
                          stock === null}
                          .invalid=${this.showValidationMessage}
                          .canOrderWithoutStock=${stock?.canOrderWithoutStock}
                          .quantity=${quantity}
                          .isQuantityAvailableForPurchase=${isQuantityAvailableForPurchase}
                          .optionQueryString=${queryString}
                          @reset-quantity=${this.resetActiveState}
                          @quantity-change=${(e: CustomEvent) => {
                            this.setActiveState(index, e.detail.quantity > 0);
                          }}
                          @quantity-stock-validation=${(event: CustomEvent) => {
                            this.validInputs = {
                              ...this.validInputs,
                              [index]: event.detail.valid,
                            };
                          }}
                        ></fg-quantity-variants-input>
                      </div>
                    `}
                ${noPriceAndQuantityInput
                  ? nothing
                  : html`
                      <div
                        class="product-totals ${classMap({
                          loading: this.loading,
                          price: totalPrice !== undefined,
                        })}"
                      >
                        <span>${totalPrice || '-'}</span>
                      </div>
                    `}
                ${this.renderQuantityInputMessage(
                  quantity,
                  isQuantityAvailableForPurchase,
                  index,
                )}
              </div>
            `;
          })}
        </div>
      </fg-configurator-step>
    `;
  }

  static styles = [
    baseStyles,
    typographyStyles,
    rootVariablesStyles,
    loadingStyles,
    css`
      .wrapper {
        display: flex;
        gap: 4px;
        flex-direction: column;
        margin: 16px 0 32px;
      }

      :host([part='wholesale']) .wrapper {
        margin-bottom: 0;
      }

      .product-variant {
        background-color: var(--fg-gray--100, var(--fg-fallback-gray--100));
        border-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
        border: 1px solid transparent;
        padding: 8px;
        display: grid;
        position: relative;
        grid-template-columns: repeat(2, minmax(90px, 1fr));
        transition:
          background-color ease-in-out ${ANIMATION_SPEED}ms,
          border-color ease-in-out ${ANIMATION_SPEED}ms,
          box-shadow ease-in-out ${ANIMATION_SPEED}ms;
      }

      .product-variant .product-quantity,
      .product-variant .product-totals,
      .product-variant fg-quantity-variant-input-message,
      .product-variant .product-stock {
        display: none;
      }

      .product-variant.expand .product-quantity,
      .product-variant.expand .product-totals,
      .product-variant.expand fg-quantity-variant-input-message,
      .product-variant.expand .product-stock {
        display: flex;
      }

      .product-variant.expand {
        background-color: #fff;
        box-shadow: var(--fg-boxshadow, var(--fg-fallback-boxshadow));
      }

      .product-variant.active {
        background-color: var(
          --fg-success--light,
          var(--fg-fallback-success--light)
        );
        border: 1px solid var(--fg-success, var(--fg-fallback-success));
      }

      .product-data {
        display: flex;
        gap: 8px;
        align-items: flex-start;
        grid-column: 1 / 3;
        grid-row: 1;
      }

      .product-data > div {
        display: flex;
        width: 100%;
        flex-direction: column;
        position: relative;
      }

      .product-data div > span {
        color: var(--fg-gray--600);
        font-size: 0.875rem;
      }

      .product-data > fg-image::part(image) {
        aspect-ratio: 1 / 1;
        max-height: 48px;
        width: 48px;
        object-fit: contain;
        background-color: #fff;
        border-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
      }

      .product-data .spacer {
        aspect-ratio: 1 / 1;
        width: 48px;
        height: 48px;
        display: block;
      }

      .product-data .caret {
        color: var(--fg-secondary, var(--fg-fallback-secondary));
        position: absolute;
        right: 8px;
        top: 24px;
      }

      .product-data .caret svg {
        display: block;
        transform: rotate(0deg);
        transition: transform ease-in-out ${ANIMATION_SPEED}ms;
      }

      .expand .product-data .caret svg {
        transform: rotate(180deg);
      }

      .product-stock {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        height: auto;
        grid-column: 1 / 3;
        grid-row: 4;
        margin-top: 8px;
      }

      .product-stock span.stock {
        color: var(--fg-success--dark, var(--fg-fallback-success--dark));
        display: flex;
        align-items: center;
        font-weight: 700;
        gap: 4px;
        margin-right: 15px;
      }

      .product-quantity,
      .product-totals {
        display: flex;
        align-items: center;
      }

      .product-stock > *:nth-child(odd) {
        background-color: var(--fg-gray--100, var(--fg-fallback-gray--100));
      }

      .product-stock-notify,
      .product-stock-amount {
        width: 100%;
        display: flex;
        justify-content: space-between;
      }

      .product-current-quantity {
        display: flex;
        justify-content: space-between;
        font-size: 0.875rem;
      }

      .product-current-quantity span {
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .product-current-quantity > span svg {
        color: var(--fg-warning, var(--fg-fallback-warning));
      }

      .product-invalid-input fg-quantity-variant-input-message {
        display: block;
      }

      .expand .product-current-quantity,
      .expand .product-invalid-input {
        display: none;
      }

      .product-current-quantity span {
        color: var(--fg-gray--600, var(--fg-fallback-gray--600));
      }

      .product-current-quantity strong {
        color: var(--fg-primary, var(--fg-fallback-primary));
      }

      .fullscreen-button {
        position: absolute;
        top: 12px;
        left: 12px;
        color: var(--fg-secondary, var(--fg-fallback-secondary));
      }

      fg-quantity-back-order {
        width: 100%;
      }

      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        .wrapper {
          gap: 8px;
        }

        .product-variant.expand {
          background-color: var(--fg-gray--100, var(--fg-fallback-gray--100));
          box-shadow: none;
        }

        .fullscreen-button {
          visibility: hidden;
        }

        .product-variant:hover .fullscreen-button {
          visibility: visible;
        }

        .product-variant .product-quantity,
        .product-variant .product-totals,
        .product-variant fg-quantity-variant-input-message,
        .product-variant .product-stock {
          display: flex;
          flex-wrap: wrap;
        }

        .product-stock > *:nth-child(odd) {
          background-color: #fff;
        }

        .product-data {
          align-items: center;
        }

        .product-data > div {
          gap: 4px;
        }

        .product-data > fg-image::part(image) {
          max-height: 72px;
          width: 72px;
        }

        .product-data .spacer {
          width: 72px;
          height: 72px;
        }

        .mobile-only {
          display: none;
        }
      }

      @media screen and (max-width: ${BREAKPOINT_EXTRA_LARGE_PX - 1}px) {
        .product-stock {
          grid-column: 1 / 3;
          grid-row: 4;
        }

        .product-totals {
          grid-column: 1 / 2;
          grid-row: 2;
        }

        .product-quantity {
          justify-content: flex-end;
          grid-column: 2 / 3;
          grid-row: 2;
        }

        fg-quantity-variant-input-message {
          margin-left: auto;
          grid-row: 3;
          grid-column: 1 / 3;
        }

        .product-stock > * {
          padding: 8px;
          margin-top: 0;
          border-radius: var(
            --fg-borderradius--xs,
            var(--fg-fallback-borderradius--xs)
          );
        }
      }

      @media screen and (min-width: ${BREAKPOINT_LARGE_PX}px) {
        .product-variant {
          column-gap: 16px;
          padding: 8px 24px 8px 8px;
          grid-template-columns: 1fr 290px;
        }
      }

      @media screen and (min-width: ${BREAKPOINT_EXTRA_LARGE_PX}px) {
        fg-quantity-variants-input {
          width: 100%;
        }

        fg-quantity-in-option {
          width: 100%;
        }

        .product-data {
          grid-column: 1 / 2;
          grid-row: 1 / 2;
        }

        .stock {
          flex-wrap: nowrap;
        }

        .product-stock {
          grid-row: 1 / 3;
          grid-column: 2 / 3;
          gap: 4px;
          justify-content: center;
          width: 100%;
        }

        fg-quantity-variant-input-message {
          grid-row: 2;
          grid-column: 3 / 4;
        }

        .product-totals.price {
          font-weight: 700;
        }

        .product-quantity,
        .product-totals {
          justify-content: flex-end;
        }

        .product-stock > *:nth-child(even) {
          background-color: transparent;
        }

        .product-stock > *:nth-child(odd) {
          background-color: transparent;
        }

        .product-stock-notify,
        .product-stock-amount {
          justify-content: flex-end;
          width: fit-content;
        }
      }

      @media screen and (min-width: ${BREAKPOINT_EXTRA_LARGE_PX}px) {
        .product-variant {
          &&:not(.noPriceAndQuantityInput) {
            grid-template-columns: 23ch 30ch 180px 105px;
          }
        }
      }
    `,
  ];
}
